/* eslint-disable prettier/prettier */
import React from 'react';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import styles from '../../styles/pages/ProgramDetailsPage.module.css';
import { AssessmentStatus } from '../../types/assessment';
import {
    EMPTY_DISPLAY_VALUE,
    formatDisplayDate,
    getFileNameWithoutExtension,
} from '../../utils/fileDisplay';
import {
    getFileCategoryLabel,
    normalizeExtension,
} from '../../utils/fileTypeMap';

interface Props {
    assessment: any;
    status?: Partial<Omit<AssessmentStatus, 'submitted_at'>> & {
        approval_status?: string;
        review_status?: string;
        passed?: boolean | null;
        submitted_at?: string | Date | null;
        file_name?: string | null;
    };
    passed?: boolean | null;
    // Renders the "File Name" and "Uploaded Date" columns (learner view)
    showFileDetails?: boolean;
    submitting: boolean;
    uploadProgress: Record<string, number>;
    onUpload: (
        e: React.ChangeEvent<HTMLInputElement>,
        assessment: any
    ) => Promise<void> | void;
    onDelete: (
        submissionId: string,
        assessmentId: string
    ) => Promise<void> | void;
}

const EMPTY_VALUE = EMPTY_DISPLAY_VALUE;

// Same format as the Due Date column ("20 Feb 26")
const formatUploadedDate = (date?: string | Date | null) =>
    formatDisplayDate(date, {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
    });

export const AssessmentRow: React.FC<Props> = ({
    assessment,
    status,
    submitting,
    uploadProgress,
    onUpload,
    onDelete,
    passed,
    showFileDetails = false,
}) => {
    const uploadDisabled =
        !assessment.allowfilesubmission || !assessment.isactive;

    const isSubmitted = Boolean(status?.has_submitted);
    const isOverdue =
        !isSubmitted &&
        assessment.duedate &&
        new Date(assessment.duedate).setHours(0, 0, 0, 0) <
            new Date().setHours(0, 0, 0, 0);



const getStatusPill = () => {
    if (status?.passed === true) {
        return {
            label: 'Approved',
            style: {
                border: '1px solid #0a7a53',
                color: '#0a7a53',
                backgroundColor: 'rgba(10, 122, 83, 0.02)',
            },
        };
    }

    if (status?.passed === false) {
        return {
            label: 'Rejected',
            style: {
                border: '1px solid #ef4444',
                color: '#ef4444',
                backgroundColor: 'rgba(239, 68, 68, 0.02)',
            },
        };
    }

    return {
        label: 'Pending',
        style: {
            border: '1px solid #f59e0b',
            color: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.02)',
        },
    };
};

const statusPill = getStatusPill();

    // Only show file details while a submission exists (deleted -> "-")
    const uploadedFileName = isSubmitted
        ? getFileNameWithoutExtension(status?.file_name)
        : EMPTY_VALUE;
    const uploadedDate = isSubmitted
        ? formatUploadedDate(status?.submitted_at)
        : EMPTY_VALUE;

    return (
        <tr className={styles.resourceRow}>
            <td className={styles.fileName}>{assessment.title}</td>

            {showFileDetails && (
                <>
                    {/* File Name Column */}
                    <td className={styles.uploadedFileCell}>
                        <span
                            className={styles.uploadedFileName}
                            title={
                                uploadedFileName !== EMPTY_VALUE
                                    ? uploadedFileName
                                    : undefined
                            }
                        >
                            {uploadedFileName}
                        </span>
                    </td>

                    {/* Uploaded Date Column */}
                    <td className={styles.uploadedDateCell}>{uploadedDate}</td>
                </>
            )}

            <td>
                {showFileDetails
                    ? getFileCategoryLabel(assessment.allowedfiletypes ?? [])
                    : assessment.allowedfiletypes?.join(' / ') || 'Document'}
            </td>
            <td>
                {assessment.duedate
                    ? new Date(assessment.duedate).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: '2-digit',
                      })
                    : 'N/A'}
            </td>

            {/* Status Column (Pill Badge) */}
            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                <span
                    style={{
                        ...statusPill.style,
                        padding: '4px 16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'inline-block',
                        minWidth: '85px',
                    }}
                >
                    {statusPill.label}
                </span>
            </td>

            {/* State Column (Icon Indicator) */}
            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                {isSubmitted ? (
                    <CheckCircle
                        className={styles.statusSubmittedIcon}
                        size={20}
                        color="#0f7652"
                        style={{ display: 'inline-block', verticalAlign: 'middle' }}
                    />
                ) : isOverdue ? (
                    <AlertCircle
                        className={styles.statusOverdueIcon}
                        size={20}
                        color="#dc2626"
                        style={{ display: 'inline-block', verticalAlign: 'middle' }}
                    />
                ) : (
                    <AlertCircle
                        className={styles.statusDueIcon}
                        size={20}
                        color="#f97316"
                        style={{ display: 'inline-block', verticalAlign: 'middle' }}
                    />
                )}
            </td>

            {/* Actions Column */}
            <td className={styles.actionsCell} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                {isSubmitted ? (
                    <button
                        className={styles.actionIcon}
                        onClick={() =>
                            status?.submission_id &&
                            onDelete(status.submission_id, assessment.id)
                        }
                        disabled={status?.is_graded}
                        title="Delete submission"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 40 40" fill="none">
                            <rect x="0.5" y="0.5" width="39" height="39" rx="4.5" fill="white" />
                            <rect x="0.5" y="0.5" width="39" height="39" rx="4.5" stroke="#D9D9D9" />
                            <path d="M18 19V25M22 19V25M27 14V28C27 28.5304 26.7893 29.0391 26.4142 29.4142C26.0391 29.7893 25.5304 30 25 30H15C14.4696 30 13.9609 29.7893 13.5858 29.4142C13.2107 29.0391 13 28.5304 13 28V14M11 14H29M16 14V12C16 11.4696 16.2107 10.9609 16.5858 10.5858C16.9609 10.2107 17.4696 10 18 10H22C22.5304 10 23.0391 10.2107 23.4142 10.5858C23.7893 10.9609 24 11.4696 24 12V14" stroke="#1E1E1E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                        <input
                            type="file"
                            id={`file-${assessment.id}`}
                            style={{ display: 'none' }}
                            accept={(assessment.allowedfiletypes ?? [])
                                .map((t: string) => normalizeExtension(t))
                                .filter(Boolean)
                                .map((ext: string) => `.${ext}`)
                                .join(',')}
                            onChange={e => onUpload(e, assessment)}
                        />

                        <button
                            className={`${styles.actionIcon} ${uploadDisabled ? styles.disabledAction : ''}`}
                            onClick={() =>
                                document
                                    .getElementById(`file-${assessment.id}`)
                                    ?.click()
                            }
                            disabled={uploadDisabled || submitting}
                            title="Upload assignment"
                        >
                            {uploadDisabled ? (
                                <Lock size={18} />
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 40 40" fill="none">
                                    <rect x="0.5" y="0.5" width="39" height="39" rx="4.5" fill="white" />
                                    <rect x="0.5" y="0.5" width="39" height="39" rx="4.5" stroke="#D9D9D9" />
                                    <path d="M20 10V23.3333M14.4444 15.5556L20 10L25.5556 15.5556M30 23.3333V27.7778C30 28.3671 29.7659 28.9324 29.3491 29.3491C28.9324 29.7659 28.3671 30 27.7778 30H12.2222C11.6329 30 11.0676 29.7659 10.6509 29.3491C10.2341 28.9324 10 28.3671 10 27.7778V23.3333" stroke="#1E1E1E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </button>

                        {uploadProgress[assessment.id] !== undefined && (
                            <div className={styles.progressWrapper}>
                                <div
                                    className={styles.progressBar}
                                    style={{
                                        width: `${uploadProgress[assessment.id]}%`,
                                    }}
                                />
                                <span>{uploadProgress[assessment.id]}%</span>
                            </div>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
};

export default AssessmentRow;